import {
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { createContext, PropsWithChildren, useEffect, useState } from 'react';
import { GetPermission } from '../firebase/messaging';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { setupNotificationUser } from '../service/social';
import { isInStandaloneMode, isIos } from '../util/platform';

type NotificationContextType = {
    isNotificationSetup: boolean;
    openModal: () => void;
};

export const NotificationContext = createContext<NotificationContextType>({
    isNotificationSetup: false,
    openModal: () => {},
});

export default NotificationContext;

export const NotificationContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { keycloak, initialized } = useKeycloak();
    const [userRespondedPermission, setUserRespondedPermission] = useLocalStorage(
        'notification_permission_answered',
        false,
    );
    const [isNotificationSetup, setIsNotificationSetup] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const openModal = () => {
        onOpen();
    };

    const getNotificationPermission = () => {
        GetPermission((token: any) => {
            if (token !== null) {
                setupNotificationUser(keycloak.token, token, keycloak.subject).then(() => {
                    setIsNotificationSetup(true);
                });
            }
        });
    };

    useEffect(() => {
        if (initialized && keycloak.authenticated && !userRespondedPermission) {
            if (isIos()) {
                if (isInStandaloneMode()) {
                    //Can't be automatic, needs to react to a click
                    onOpen();
                }
            } else {
                //get permission automatically on android/desktop
                getNotificationPermission();
            }
        }
    }, [keycloak, initialized]);

    return (
        <NotificationContext.Provider value={{ isNotificationSetup, openModal }}>
            <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay className="notification-modal-overlay" />
                <ModalContent>
                    <ModalHeader className="notification-modal-text">
                        ENABLE PUSH NOTIFICATIONS?
                    </ModalHeader>
                    <ModalBody>
                        <Text className="notification-modal-text">
                            GET NOTIFIED SO YOU DON'T MISS WHEN YOUR FAVOURITE STREAMERS GO LIVE.
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <Text
                            className="notification-modal-button notification-modal-confirm"
                            colorScheme={'blue'}
                            onClick={() => {
                                getNotificationPermission();
                                setUserRespondedPermission(true);
                                onClose();
                            }}
                        >
                            ENABLE
                        </Text>
                        <Text
                            className="notification-modal-button notification-modal-cancel"
                            variant="ghost"
                            onClick={() => {
                                setUserRespondedPermission(true);
                                onClose();
                            }}
                        >
                            NO THANKS
                        </Text>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            {children}
        </NotificationContext.Provider>
    );
};
